/**
 * The mark inside an action button.
 *
 * The four controls on a card used to be four words of identical weight, differing only by fill:
 * "Copy reply and open comment", "Open without copying", "Mark as posted", "Remove". Read down
 * the row and nothing told you which was the next step and which merely undid something - the
 * reader had to parse three phrases to find the verb, and the verb was never the first word.
 *
 * Two changes fix that, and this file is one of them. The labels are now the verbs ("Copy →
 * Open", "Open", "Posted", "Remove") and each carries a shape, so the ordering is legible before
 * the words are read. The other half is colour, in styles.css: the solid button is the next step,
 * green is the thing that got posted, red is the way out.
 *
 * Inline SVG in currentColor, for the same reasons as PlatformIcon: no request, no dependency, no
 * flash of a missing glyph - and currentColor means the icon follows the button's own colour, so
 * a quiet green "Posted" and a solid green one need no second rule.
 *
 * Drawn as outlines at 1.8px on a 24px grid, which stays crisp at the 16px they render at. They
 * are decorative here - every button says what it does in words - so they are hidden from
 * assistive tech rather than given a label of their own.
 */

export type ActionIconName = 'copy' | 'open' | 'posted' | 'remove' | 'refresh' | 'note' | 'send';

interface Props {
  name: ActionIconName;
  size?: number;
}

export default function ActionIcon({ name, size = 16 }: Props) {
  return (
    <svg
      className="action-icon"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {glyph(name)}
    </svg>
  );
}

function glyph(name: ActionIconName) {
  switch (name) {
    case 'copy':
      // Two sheets, the back one offset: the standard "this goes on the clipboard" shape.
      return (
        <>
          <rect x="9" y="9" width="11.5" height="11.5" rx="2.4" />
          <path d="M15.2 5.6v-.4A2.2 2.2 0 0 0 13 3H5.2A2.2 2.2 0 0 0 3 5.2V13a2.2 2.2 0 0 0 2.2 2.2h.4" />
        </>
      );

    case 'open':
      // An arrow leaving a box: opens outside this tab, which is what the anchor really does.
      return (
        <>
          <path d="M13.8 4.2H20v6.2" />
          <path d="M20 4.2 11.4 12.8" />
          <path d="M18.2 14.4V19a2.2 2.2 0 0 1-2.2 2.2H5.2A2.2 2.2 0 0 1 3 19V8.2A2.2 2.2 0 0 1 5.2 6h4.6" />
        </>
      );

    case 'posted':
      // A bare tick, a touch heavier than the others: it is the only affirmative mark on the card.
      return <path d="M4.6 12.6 9.3 17.3 19.4 7" strokeWidth={2.1} />;

    case 'remove':
      return (
        <>
          <path d="M4 6.8h16" />
          <path d="M9.6 6.8V4.6h4.8v2.2" />
          <path d="M6.3 6.8l.9 12.3A2 2 0 0 0 9.2 21h5.6a2 2 0 0 0 2-1.9l.9-12.3" />
          <path d="M10.4 10.8v6.2M13.6 10.8v6.2" />
        </>
      );

    case 'refresh':
      // A circle with a gap at the top-right and an arrowhead closing it: the same inputs, written
      // again. A circular arrow rather than a "+" because the action rewrites a reply, it does not
      // add one - and the reply underneath is what changes.
      //
      // The sweep flag is load-bearing, not decoration: with `0` the arc picks the other circle
      // through the same two points, which centres above the viewbox and renders as two clipped
      // fragments. `1` draws the intended circle, and the head turns to meet it.
      return (
        <>
          <path d="M16.4 6.2A7.6 7.6 0 1 1 12 4.8" />
          <path d="M10.1 2.7 12 4.8l-1.9 2.1" />
        </>
      );

    case 'note':
      // A pencil. The watchlist has no room for a fourth line of prose, so the note it opens is
      // a mark rather than a word - and the button carries the `aria-label` that this icon,
      // being decorative, does not.
      return (
        <>
          <path d="M16.6 3.7a2.5 2.5 0 0 1 3.5 3.5L7.4 19.9 3.3 21.2l1.3-4.1z" />
          <path d="M15.1 5.2 18.6 8.7" />
        </>
      );

    case 'send':
      // A paper plane. "Auto post" hands the reply to the local poster rather than pasting it by
      // hand, and the mark says "send", not "sent" - the tick already owns sent.
      return (
        <>
          <path d="M21 3 10.6 13.4" />
          <path d="M21 3 14.4 21l-3.8-7.6L3 9.6z" />
        </>
      );
  }
}
